import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Card,
  Button,
  Alert,
  Spin,
  Empty,
  Tag,
  Row,
  Col,
  Progress,
  Space,
  Switch,
  Typography,
  Divider,
  Form,
  Input,
  message,
} from 'antd';
import { authService } from '../services/auth.service';
import {
  PASSWORD_POLICY_HINT,
  PASSWORD_POLICY_MESSAGE,
  PASSWORD_POLICY_REGEX,
} from '../utils/password-policy';
import { Notice } from '../components/common/Notice';
import {
  EditOutlined,
  CloseOutlined,
  SaveOutlined,
  UserOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  FundOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { ParticipantProfile, UpdateProfileData } from '../types/profile.types';
import { profileService } from '../services/profile.service';
import { ProfileForm } from '../components/profile/ProfileForm';
import { genderLabels, educationLabels, economicLabels } from '../utils/profile-labels';
import { calculateAgeRange, getAgeRangeLabel } from '../utils/age-range';
import { validationService } from '../services/validation.service';
import { legalConsentService } from '../services/legalConsent.service';
import type { ValidationProfileResponse } from '../types/validation.types';
import ValidationLevelCard from '../components/profile/ValidationLevelCard';
import { ValidationUpgradeWizard } from '../components/profile/ValidationUpgradeWizard';
import LegalConsentStatusCard from '../components/profile/LegalConsentStatusCard';
import LegalConsentBlockingModal from '../components/common/LegalConsentBlockingModal';
import { CookieSettingsModal } from '../components/consent/CookieSettingsModal';
import { emailConsentService } from '../services/emailConsent.service';
import { useAuth } from '../hooks/useAuth';
import { marketingService } from '../services/marketing.service';
import type { LegalDocumentType } from '../types/legalConsent.types';
import './ProfilePage.css';

const { Title, Text } = Typography;

const formId = 'profile-edit-form';

function isLegalConsentRequiredError(err: any): boolean {
  const apiError = err?.response?.data?.error;
  const apiMessage = err?.response?.data?.message;
  return apiError === 'LEGAL_CONSENT_REQUIRED'
    || /LEGAL_CONSENT_REQUIRED|Aceite dos termos obrigatorios pendente/i.test(`${apiError || ''} ${apiMessage || ''}`);
}

const completionFields: { key: keyof UpdateProfileData; label: string }[] = [
  { key: 'gender', label: 'Gênero' },
  { key: 'dateOfBirth', label: 'Data de nascimento' },
  { key: 'educationLevel', label: 'Escolaridade' },
  { key: 'economicLevel', label: 'Classe econômica' },
  { key: 'cityId', label: 'Cidade' },
  { key: 'neighborhood', label: 'Bairro' },
];

function getCompletion(values: Partial<UpdateProfileData>): number {
  const completed = completionFields.filter(({ key }) => Boolean(values[key])).length;
  return Math.round((completed / completionFields.length) * 100);
}

function profileToFormValues(profile: ParticipantProfile | null): Partial<UpdateProfileData> {
  if (!profile) return {};

  return {
    preferredName: profile.preferredName,
    phone: profile.phone,
    gender: profile.gender,
    dateOfBirth: profile.dateOfBirth?.split('T')[0],
    educationLevel: profile.educationLevel,
    economicLevel: profile.economicLevel,
    stateId: profile.city?.stateId,
    cityId: profile.cityId,
    neighborhood: profile.neighborhood,
  };
}

export const ProfilePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { legalConsentStatus, refreshLegalConsentStatus, refreshSession, user } = useAuth();
  const [profile, setProfile] = useState<ParticipantProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [draftValues, setDraftValues] = useState<Partial<UpdateProfileData>>({});
  const [commPrefsModalOpen, setCommPrefsModalOpen] = useState(false);
  const [highlightCommPrefs, setHighlightCommPrefs] = useState(false);
  const commPrefsCardRef = useRef<HTMLDivElement>(null);
  // Default true: habilitado por padrão para usuários legados e novos até que
  // o fetch resolva (evita "piscar" desligado) — mesma regra do backend
  // (ParticipantProfile.emailMarketingConsent tem DEFAULT true no banco).
  const [emailConsent, setEmailConsent] = useState(true);
  const [savingEmailConsent, setSavingEmailConsent] = useState(false);

  // FR-019/FR-022: link no rodapé do email de comunicação em massa aponta para
  // /profile?highlight=comunicacao — rola/destaca esta seção ao chegar aqui.
  // Depende também de `loading`/`profile`: a seção só existe no DOM depois que o
  // perfil termina de carregar (sidebar condicional a !editMode && profile).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('highlight') === 'comunicacao' && !loading && commPrefsCardRef.current) {
      setHighlightCommPrefs(true);
      commPrefsCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const timer = setTimeout(() => setHighlightCommPrefs(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [location.search, loading]);
  const [validationSummary, setValidationSummary] = useState<ValidationProfileResponse | null>(null);
  const [showValidationWizard, setShowValidationWizard] = useState(false);
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [acceptingDocumentType, setAcceptingDocumentType] = useState<LegalDocumentType | null>(null);
  const [hasPassword, setHasPassword] = useState<boolean | null>(null);
  const [settingPassword, setSettingPassword] = useState(false);
  const [passwordForm] = Form.useForm<{ newPassword: string; confirmPassword: string }>();

  const isEditRoute = location.pathname === '/profile/edit';

  useEffect(() => {
    loadProfile();
    loadValidationSummary();
    loadAuthProviderStatus();
    loadEmailConsent();
  }, []);

  const loadAuthProviderStatus = async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      setHasPassword(Boolean(currentUser?.hasPassword));
    } catch {
      setHasPassword(null);
    }
  };

  const loadEmailConsent = async () => {
    const consent = await emailConsentService.fetchConsent();
    // Se o fetch falhar (fetchConsent engole erro e retorna null), mantém o
    // default true em vez de sugerir que o usuário está desinscrito.
    setEmailConsent(consent?.marketingConsent ?? true);
  };

  const handleEmailConsentChange = async (checked: boolean) => {
    const previous = emailConsent;
    setEmailConsent(checked);
    setSavingEmailConsent(true);
    try {
      await emailConsentService.updateConsent(checked);
      message.success(
        checked ? 'Você voltará a receber emails de campanha.' : 'Você não receberá mais emails de campanha.'
      );
    } catch {
      setEmailConsent(previous);
      message.error('Não foi possível salvar sua preferência. Tente novamente.');
    } finally {
      setSavingEmailConsent(false);
    }
  };

  const handleSetPassword = async (values: { newPassword: string }) => {
    setSettingPassword(true);
    try {
      await authService.setPassword(values.newPassword);
      message.success('Senha definida com sucesso!');
      passwordForm.resetFields();
      setHasPassword(true);
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Não foi possível definir a senha.');
    } finally {
      setSettingPassword(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('tab') === 'validation') {
      setShowValidationWizard(true);
    }
  }, [location.search]);

  useEffect(() => {
    const shouldEdit = (isEditRoute || Boolean(location.state?.isEditing)) && !!profile && !showCreateForm;
    setIsEditing(shouldEdit);
  }, [isEditRoute, location.state, profile, showCreateForm]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await profileService.getProfile();
      setProfile(data);
      setDraftValues(profileToFormValues(data));
      setShowCreateForm(false);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setShowCreateForm(true);
        setDraftValues({});
      } else {
        setError(err.message || 'Falha ao carregar perfil');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadValidationSummary = async () => {
    try {
      const summary = await validationService.getProfileValidation();
      setValidationSummary(summary);
    } catch {
      setValidationSummary(null);
    }
  };

  const syncAuthUser = (updated: ParticipantProfile) => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) return;
    const parsed = JSON.parse(storedUser);
    localStorage.setItem('user', JSON.stringify({
      ...parsed,
      preferredName: updated.preferredName ?? parsed.preferredName,
      phone: updated.phone ?? parsed.phone,
    }));
    refreshSession();
  };

  const handleCreate = async (data: any) => {
    try {
      const created = await profileService.createProfile(data);
      marketingService.pushEvent('perfil_completo', { user_id: user?.userId ?? '' });
      setProfile(created);
      setDraftValues(profileToFormValues(created));
      syncAuthUser(created);
      setShowCreateForm(false);
      setError(null);
      const nextUrl = new URLSearchParams(location.search).get('next');
      navigate(nextUrl?.startsWith('/') ? nextUrl : '/profile');
    } catch (err: any) {
      if (isLegalConsentRequiredError(err)) {
        setShowLegalModal(true);
        await refreshLegalConsentStatus();
      }
      setError(err.message || 'Falha ao criar perfil');
    }
  };

  const handleUpdate = async (data: any) => {
    try {
      const previousPhone = profile?.phone;
      const updated = await profileService.updateProfile(data);
      marketingService.pushEvent('perfil_completo', { user_id: user?.userId ?? '' });
      setProfile(updated);
      setDraftValues(profileToFormValues(updated));
      syncAuthUser(updated);
      const phoneChanged = Boolean(previousPhone && data.phone && data.phone !== previousPhone);
      if (phoneChanged) {
        setShowValidationWizard(true);
      }
      await loadValidationSummary();
      await refreshLegalConsentStatus();
      setIsEditing(false);
      const nextUrl = new URLSearchParams(location.search).get('next');
      if (nextUrl?.startsWith('/')) {
        navigate(nextUrl);
      } else if (isEditRoute) {
        navigate('/profile');
      }
    } catch (err: any) {
      if (isLegalConsentRequiredError(err)) {
        setShowLegalModal(true);
        await refreshLegalConsentStatus();
      }
      throw err;
    }
  };

  const handleCancelEdit = () => {
    if (showCreateForm) {
      navigate('/');
      return;
    }

    setDraftValues(profileToFormValues(profile));
    setIsEditing(false);

    if (isEditRoute) {
      navigate('/profile');
    }
  };

  const handleAcceptDocument = async (documentType: LegalDocumentType, version: string) => {
    try {
      setAcceptingDocumentType(documentType);
      await legalConsentService.registerConsent({
        documentType,
        version,
        accepted: true,
      });
      await refreshLegalConsentStatus();
      message.success('Aceite registrado com sucesso.');
    } catch (err: any) {
      message.error(err?.message || 'Não foi possível registrar o aceite.');
    } finally {
      setAcceptingDocumentType(null);
    }
  };

  const currentValues = useMemo(() => ({
    ...profileToFormValues(profile),
    ...draftValues,
  }), [profile, draftValues]);

  const profileInitialData = useMemo(() => profile ? {
    preferredName: profile.preferredName,
    phone: profile.phone,
    gender: profile.gender,
    dateOfBirth: profile.dateOfBirth.split('T')[0],
    educationLevel: profile.educationLevel,
    economicLevel: profile.economicLevel,
    stateId: profile.city?.stateId,
    cityId: profile.cityId,
    neighborhood: profile.neighborhood,
  } : undefined, [profile]);

  const completion = getCompletion(currentValues);
  const hasPendingLegalConsent = Boolean(
    legalConsentStatus
    && (!legalConsentStatus.fullyCompliant || legalConsentStatus.blockedActions.includes('SUBMIT_FORM'))
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Alert type="error" message="Erro" description={error} />
      </div>
    );
  }

  if (!profile && !showCreateForm) {
    return <Empty description="Perfil não encontrado" />;
  }

  const editMode = showCreateForm || isEditing;

  return (
    <div className="profile-page-wrap">
      <div className="profile-page-content">
        <Card className="profile-hero-card" bordered={false}>
          <div className="profile-hero-inner">
            <Space direction="vertical" size={2}>
              <Title level={4} style={{ margin: 0 }}>
                {showCreateForm ? 'Criar meu perfil' : 'Meu Perfil'}
              </Title>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {showCreateForm
                  ? 'Preencha os dados para liberar participação em pesquisas.'
                  : 'Mantenha seu perfil atualizado para continuar respondendo pesquisas relevantes.'}
              </Text>
              {profile?.isStale && (
                <Tag color="gold">Perfil desatualizado há mais de 90 dias</Tag>
              )}
            </Space>
            <div className="profile-progress-inline">
              <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>Completude</Text>
              <Progress percent={completion} size="small" />
            </div>
          </div>
        </Card>

        {profile?.isStale && !editMode && (
          <Notice
            message="Seu perfil está desatualizado"
            description="Atualize agora para continuar participando de pesquisas sem interrupções."
            type="warning"
            showIcon
            className="mb-4"
          />
        )}

        <Row gutter={[20, 20]}>
          <Col xs={24} lg={editMode ? 24 : 16}>
            <Card
              bordered={false}
              className="profile-main-card"
              extra={!editMode && profile ? (
                <Button size="small" icon={<EditOutlined />} onClick={() => setIsEditing(true)}>
                  Editar
                </Button>
              ) : undefined}
            >
              {!editMode && profile && (
                <>
                  <div className="profile-section">
                    <div className="profile-section-header">
                      <UserOutlined className="profile-section-icon" />
                      <Text strong>Dados da conta</Text>
                    </div>
                    <div className="profile-grid">
                      <div>
                        <Text type="secondary">Nome de exibição</Text>
                        <p>{profile.preferredName || '-'}</p>
                      </div>
                      <div>
                        <Text type="secondary">Telefone</Text>
                        <p>
                          {profile.phone || '-'}{' '}
                          <Tag color={validationSummary && validationSummary.validationLevel !== 'LEVEL_1' ? 'green' : 'orange'}>
                            {validationSummary && validationSummary.validationLevel !== 'LEVEL_1' ? 'Validado' : 'Não validado'}
                          </Tag>
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="profile-section">
                    <div className="profile-section-header">
                      <PhoneOutlined className="profile-section-icon" />
                      <Text strong>Dados pessoais</Text>
                    </div>
                    <div className="profile-grid">
                      <div>
                        <Text type="secondary">Gênero</Text>
                        <p>{genderLabels[profile.gender] || profile.gender}</p>
                      </div>
                      <div>
                        <Text type="secondary">Faixa etária</Text>
                        <p>{getAgeRangeLabel(calculateAgeRange(profile.dateOfBirth))}</p>
                      </div>
                    </div>
                  </div>

                  <div className="profile-section">
                    <div className="profile-section-header">
                      <FundOutlined className="profile-section-icon" />
                      <Text strong>Contexto socioeconômico</Text>
                    </div>
                    <div className="profile-grid">
                      <div>
                        <Text type="secondary">Escolaridade</Text>
                        <p>{educationLabels[profile.educationLevel] || profile.educationLevel}</p>
                      </div>
                      <div>
                        <Text type="secondary">Classe econômica</Text>
                        <p>{economicLabels[profile.economicLevel] || profile.economicLevel}</p>
                      </div>
                    </div>
                  </div>

                  <div className="profile-section">
                    <div className="profile-section-header">
                      <EnvironmentOutlined className="profile-section-icon" />
                      <Text strong>Localização</Text>
                    </div>
                    <div className="profile-grid">
                      <div>
                        <Text type="secondary">Cidade</Text>
                        <p>{profile.city ? `${profile.city.name} - ${profile.city.state?.code}` : '-'}</p>
                      </div>
                      <div>
                        <Text type="secondary">Bairro</Text>
                        <p>{profile.neighborhood}</p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {editMode && (
                <>
                  {showCreateForm && (
                    <Notice
                      message="Complete seu perfil"
                      description="Preencha os campos abaixo para começar a participar de pesquisas."
                      type="info"
                      showIcon
                      className="mb-6"
                    />
                  )}

                  {hasPendingLegalConsent && (
                    <Notice
                      message="Aceite de termos obrigatório para salvar"
                      description="Para concluir alterações no perfil, revise e aceite os documentos legais pendentes abaixo."
                      type="warning"
                      showIcon
                      className="mb-4"
                    />
                  )}

                  <div className="mb-3 rounded-xl border border-slate-200 p-4">
                    <LegalConsentStatusCard
                      status={legalConsentStatus}
                      onAcceptDocument={handleAcceptDocument}
                      acceptingDocumentType={acceptingDocumentType}
                    />
                  </div>

                  <ProfileForm
                    formId={formId}
                    initialData={profileInitialData}
                    onSubmit={showCreateForm ? handleCreate : handleUpdate}
                    isUpdate={!showCreateForm}
                    validationLevel={validationSummary?.validationLevel ?? null}
                    showSubmitButton={false}
                    onValuesChange={(_, allValues) => setDraftValues(allValues)}
                  />
                </>
              )}
            </Card>
          </Col>

          {!editMode && <Col xs={24} lg={8}>
            <Card bordered={false} className="profile-side-card">
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <div className="flex items-center justify-between">
                  <Text strong>Resumo rápido</Text>
                  {profile && <Tag color="blue">v{profile.profileVersion}</Tag>}
                </div>

                <div className="profile-meta-row">
                  <div>
                    <Text type="secondary" className="profile-meta-label">Última atualização</Text>
                    <Text strong className="profile-meta-value">
                      {profile ? new Date(profile.lastUpdatedAt).toLocaleDateString('pt-BR') : '-'}
                    </Text>
                  </div>
                  <div>
                    <Text type="secondary" className="profile-meta-label">Status</Text>
                    <Text strong className="profile-meta-value">
                      {profile?.isStale ? 'Desatualizado' : 'Atualizado'}
                    </Text>
                  </div>
                </div>

                <Divider style={{ margin: '6px 0' }} />

                {hasPassword === false && (
                  <Card title="Definir senha" size="small">
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
                      Sua conta usa apenas o login com Google. Defina uma senha para garantir acesso
                      mesmo que você perca o acesso à sua conta Google.
                    </Text>
                    <Form form={passwordForm} layout="vertical" onFinish={handleSetPassword}>
                      <Form.Item
                        label="Nova senha"
                        name="newPassword"
                        rules={[
                          { required: true, message: 'Informe a nova senha!' },
                          { min: 8, message: 'Senha deve ter no mínimo 8 caracteres!' },
                          { pattern: PASSWORD_POLICY_REGEX, message: PASSWORD_POLICY_MESSAGE },
                        ]}
                        extra={PASSWORD_POLICY_HINT}
                      >
                        <Input.Password prefix={<LockOutlined />} placeholder="••••••••" disabled={settingPassword} />
                      </Form.Item>
                      <Form.Item
                        label="Confirmar senha"
                        name="confirmPassword"
                        dependencies={['newPassword']}
                        rules={[
                          { required: true, message: 'Confirme a nova senha!' },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              if (!value || getFieldValue('newPassword') === value) {
                                return Promise.resolve();
                              }
                              return Promise.reject(new Error('As senhas não coincidem!'));
                            },
                          }),
                        ]}
                      >
                        <Input.Password prefix={<LockOutlined />} placeholder="••••••••" disabled={settingPassword} />
                      </Form.Item>
                      <Button type="primary" htmlType="submit" loading={settingPassword} block>
                        Definir senha
                      </Button>
                    </Form>
                  </Card>
                )}

                {validationSummary && (
                  <ValidationLevelCard
                    summary={validationSummary}
                    onUpgrade={() => setShowValidationWizard(true)}
                  />
                )}

                {showValidationWizard && (
                  <Card title="Elevar nível de validação" size="small">
                    <ValidationUpgradeWizard
                      initialPhone={profile?.phone}
                      onLevelUp={async () => {
                        setShowValidationWizard(false);
                        await loadValidationSummary();
                        await refreshLegalConsentStatus();
                      }}
                    />
                  </Card>
                )}

                <LegalConsentStatusCard
                  status={legalConsentStatus}
                  onAcceptDocument={handleAcceptDocument}
                  acceptingDocumentType={acceptingDocumentType}
                />

                <div
                  ref={commPrefsCardRef}
                  style={
                    highlightCommPrefs
                      ? { boxShadow: '0 0 0 3px #2E2B81', borderRadius: 8, transition: 'box-shadow 0.3s' }
                      : undefined
                  }
                >
                  <Card size="small" title="Preferências de Comunicação">
                    <Space direction="vertical" style={{ width: '100%' }} size={16}>
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Text strong style={{ fontSize: 13 }}>Emails de campanha da PePoll</Text>
                          <Switch
                            checked={emailConsent}
                            loading={savingEmailConsent}
                            onChange={handleEmailConsentChange}
                          />
                        </div>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                          Novidades, premiações e convites para pesquisas por email. Não afeta os emails
                          transacionais da sua conta (confirmação de cadastro, recuperação de senha etc.).
                        </Text>
                      </div>
                      <Divider style={{ margin: 0 }} />
                      <div>
                        <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
                          Cookies de marketing (Google Tag Manager e Meta Pixel) usados para medir o
                          alcance de campanhas.
                        </Text>
                        <Button onClick={() => setCommPrefsModalOpen(true)}>
                          Gerenciar cookies
                        </Button>
                      </div>
                    </Space>
                  </Card>
                </div>

              </Space>
            </Card>
          </Col>}
        </Row>

        {!editMode && profile && (
          <div className="profile-footer-note">
            <Divider style={{ margin: '16px 0 12px' }} />
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.6, display: 'block' }}>
                <Text strong style={{ fontSize: 12 }}>Finalidade dos dados do perfil</Text>
                {' — '}
                Seus dados demográficos (gênero, faixa etária, escolaridade, classe econômica e localização) são utilizados exclusivamente para identificar pesquisas relevantes ao seu perfil e para estratificação de resultados em relatórios agregados e anonimizados, em conformidade com o Art. 7º, inciso V da Lei 13.709/2018 (LGPD). Nenhum dado de identificação pessoal é compartilhado individualmente com pesquisadores.
              </Text>
              <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.6, display: 'block' }}>
                <Text strong style={{ fontSize: 12 }}>Consentimento legal</Text>
                {' — '}
                Você pode revisar os{' '}
                <RouterLink to="/legal/termos-de-uso" style={{ fontSize: 12 }}>Termos de Uso</RouterLink>
                {' e a '}
                <RouterLink to="/legal/politica-de-privacidade" style={{ fontSize: 12 }}>Política de Privacidade</RouterLink>
                {' '}a qualquer momento. Caso a plataforma publique uma versão atualizada de algum documento, será necessário aceitar os novos termos para continuar participando de pesquisas. Para exercer seus direitos de acesso, correção, portabilidade ou exclusão previstos na LGPD, entre em contato:{' '}
                <a href="mailto:privacidade@pepoll.app" style={{ fontSize: 12 }}>privacidade@pepoll.app</a>
                {' '}(prazo de resposta: 15 dias úteis).
              </Text>
              <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.6, display: 'block' }}>
                <Text strong style={{ fontSize: 12 }}>Nível de validação</Text>
                {' — '}
                O nível de validação indica o grau de verificação da sua identidade na plataforma. O Nível 1 é obtido automaticamente com o cadastro por e-mail. O Nível 2 exige validação do telefone por código SMS ou WhatsApp e amplia o acesso a pesquisas que requerem maior confiabilidade. O Nível 3 requer validação de documento oficial (RG ou CNH).
              </Text>
            </Space>
          </div>
        )}

        {editMode && (
          <div className="profile-sticky-actions">
            <div className="profile-sticky-inner">
              <Button icon={<CloseOutlined />} onClick={handleCancelEdit}>
                Cancelar
              </Button>
              <Button type="primary" icon={<SaveOutlined />} htmlType="submit" form={formId}>
                {showCreateForm ? 'Salvar e continuar' : 'Salvar alterações'}
              </Button>
            </div>
          </div>
        )}

        <LegalConsentBlockingModal
          open={showLegalModal}
          onClose={() => setShowLegalModal(false)}
          onReview={() => {
            setShowLegalModal(false);
            navigate('/legal/termos-de-uso');
          }}
        />

        <CookieSettingsModal
          open={commPrefsModalOpen}
          onClose={() => setCommPrefsModalOpen(false)}
        />
      </div>
    </div>
  );
};
